/**
 * useAttemptRecorder - posting attempts to the engine without the child ever
 * knowing it is happening.
 *
 * PLATFORM SCAFFOLD. Used by every game; the only per-game part is the gameId.
 *
 * Wraps logic/attemptQueue.js (which holds the rules and is tested) in the
 * React lifecycle (which does not).
 *
 * ---------------------------------------------------------------------------
 * WHAT IT SENDS
 *
 * POST /api/engine/attempts, one body per trial:
 *
 *   childId, gameId, phase, level, step
 *   components   ONLY the boxes this game owns. Phase 2 sends none at all -
 *                the engine judges all three from the trial record, and
 *                asserting them would be refused.
 *   trial        the whole COMPONENT-JUDGEMENT.md section 4 record
 *   attemptNumber, recognition {confidence, alternatives}, supportIncreases
 *
 * The server answers with the box colours it wrote and a section 8.4 pacing
 * recommendation computed from the child's whole history - which is better
 * than anything this session can compute - so `onRecommendation` hands that
 * back to the game.
 *
 * ---------------------------------------------------------------------------
 * PRACTICE MODE
 *
 * With no child selected nothing is posted, because there is no chart to post
 * to. The game is fully playable; `status.practice` is true and the grown-ups
 * panel says so. An adult trying the game out must not silently write into
 * some other child's record, and a child whose profile has not been created
 * yet must not be turned away at the door.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { engineApi } from '../../../utils/api';
import { createAttemptQueue } from './attemptQueue.js';

const DRAIN_INTERVAL_MS = 2500;

export function useAttemptRecorder({ childId, gameId, onRecommendation }) {
  const [status, setStatus] = useState({
    pending: 0, sent: 0, rejected: 0, stopped: false, lastError: null, practice: !childId,
  });

  const onRecommendationRef = useRef(onRecommendation);
  onRecommendationRef.current = onRecommendation;

  const rejectionsRef = useRef([]);

  const queue = useMemo(() => createAttemptQueue({
    post: async (payload) => {
      const result = await engineApi.recordAttempt(payload);
      if (result && result.recommendation && onRecommendationRef.current) {
        onRecommendationRef.current(result.recommendation, result, payload);
      }
      return result;
    },
    onChange: (next) => setStatus((prev) => ({ ...prev, ...next, practice: false })),
    onRejected: (item, error) => {
      // A 400 from the engine means this game measured the wrong set of boxes
      // for this level. It is a bug in the game and it will happen on every
      // attempt until it is fixed, so it is kept where a developer can see it
      // rather than being retried forever or swallowed.
      rejectionsRef.current = [
        ...rejectionsRef.current,
        { at: Date.now(), message: error.message, details: error.details, payload: item.payload },
      ].slice(-10);
      // eslint-disable-next-line no-console
      console.error(`[${gameId}] the engine refused an attempt:`, error.message, error.details);
    },
  }), []);

  // Drain on a timer. Nothing in the game ever awaits this.
  useEffect(() => {
    if (!childId) return undefined;
    const id = setInterval(() => { queue.drain(); }, DRAIN_INTERVAL_MS);
    return () => clearInterval(id);
  }, [queue, childId]);

  useEffect(() => {
    setStatus((prev) => ({ ...prev, practice: !childId }));
  }, [childId]);

  /**
   * @param {object} attempt
   * @param {number} attempt.phase
   * @param {number} attempt.level
   * @param {number} attempt.step
   * @param {Object<string,object>} attempt.components
   * @param {object} attempt.trial          raw trial record
   * @param {number} attempt.attemptNumber
   * @param {string} attempt.expected
   * @param {string|null} attempt.heard
   * @param {string|null} attempt.rawTranscript
   * @param {{confidence:number|null, alternatives:Array}} attempt.recognition
   * @param {number} attempt.supportIncreases
   * @returns {number|null} queue id, or null in practice mode
   */
  const record = useCallback((attempt) => {
    if (!childId) return null;
    const id = queue.enqueue({ childId, gameId, ...attempt });
    // Try immediately; the interval is only a safety net.
    queue.drain();
    return id;
  }, [queue, childId, gameId]);

  const flush = useCallback(() => queue.drain(), [queue]);

  return {
    record,
    flush,
    status,
    rejections: () => rejectionsRef.current,
    unsent: () => queue.drainable(),
  };
}

export default useAttemptRecorder;
